/* Companies that moved further than you asked to be told about.
 *
 * Kept apart from the rule engine on purpose. Those alerts are about your own
 * limits — a weight over its ceiling, a review gone past its date — and they are
 * yours whether the market is open or shut. This is the market doing something,
 * which is a different kind of fact and does not belong in the same list.
 *
 * Nothing here recommends anything (§1). It reports a move and its size. */

import type { Quote, Watch } from '../types';
import type { FullPosition } from './derive';

export interface Mover {
  symbol: string;
  label: string;
  changePct: number;
  /** Held, as against merely watched — one of these costs you money today. */
  owned: boolean;
}

export interface MoveWatchInput {
  quotes: Quote[];
  positions: FullPosition[];
  watchlist: Watch[];
  thresholdPct: number;
  /** Symbols the user asked not to hear about. */
  muted?: string[];
}

/** Day move against the previous close, for everything the book follows. */
export function movers(input: MoveWatchInput): Mover[] {
  const { quotes, positions, watchlist, thresholdPct } = input;
  if (!(thresholdPct > 0)) return [];
  const muted = new Set((input.muted ?? []).map((s) => s.toUpperCase()));

  const labels = new Map<string, { label: string; owned: boolean }>();
  for (const w of watchlist) labels.set(w.yahooSymbol, { label: w.ticker, owned: false });
  // A held name wins the label: it is the one you think of it by.
  for (const p of positions) labels.set(p.yahooSymbol, { label: p.ticker, owned: true });

  const out: Mover[] = [];
  for (const q of quotes) {
    const known = labels.get(q.symbol);
    if (!known || muted.has(q.symbol.toUpperCase())) continue;
    if (!(q.previousClose > 0)) continue;

    const changePct = ((q.price - q.previousClose) / q.previousClose) * 100;
    if (Math.abs(changePct) < thresholdPct) continue;
    out.push({ symbol: q.symbol, label: known.label, changePct, owned: known.owned });
  }

  // Biggest move first, whichever way it went — a fall of nine matters as much
  // as a rise of nine.
  return out.sort((a, b) => Math.abs(b.changePct) - Math.abs(a.changePct));
}

/** One line, in the same plain words the rest of the app uses (§4.2). */
export function moverSentence(m: Mover): string {
  const direction = m.changePct >= 0 ? 'up' : 'down';
  return `${m.label} is ${direction} ${Math.abs(m.changePct).toFixed(1)}% today.`;
}
